/** Get/set nested paths in ECharts option objects (e.g. "series.0.smooth"). */

export function getOptionPath(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    const key = /^\d+$/.test(part) ? Number(part) : part;
    current = (current as Record<string | number, unknown>)[key];
  }
  return current;
}

export function setOptionPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const parts = path.split(".");
  const root = deepClone(obj);
  let current: Record<string | number, unknown> = root;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const key = /^\d+$/.test(part) ? Number(part) : part;
    const nextPart = parts[i + 1];
    const nextIsIndex = /^\d+$/.test(nextPart);
    let next = current[key];
    if (next == null || typeof next !== "object") {
      next = nextIsIndex ? [] : {};
      current[key] = next;
    }
    current = next as Record<string | number, unknown>;
  }

  const last = parts[parts.length - 1];
  const lastKey = /^\d+$/.test(last) ? Number(last) : last;
  if (value === undefined || value === null || value === "") {
    delete current[lastKey];
  } else {
    current[lastKey] = value;
  }
  return root;
}

export function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>
): Record<string, unknown> {
  const result = deepClone(base);
  for (const [key, val] of Object.entries(override)) {
    if (
      val &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      result[key] &&
      typeof result[key] === "object" &&
      !Array.isArray(result[key])
    ) {
      result[key] = deepMerge(result[key] as Record<string, unknown>, val as Record<string, unknown>);
    } else {
      result[key] = val;
    }
  }
  return result;
}

const PRESENTATION_KEYS = [
  "title",
  "legend",
  "tooltip",
  "grid",
  "visualMap",
  "calendar",
  "radar",
  "backgroundColor",
  "color",
  "singleAxis",
  "parallel",
] as const;

const SERIES_STYLE_KEYS = [
  "itemStyle",
  "lineStyle",
  "areaStyle",
  "label",
  "emphasis",
  "smooth",
  "showSymbol",
  "radius",
  "roseType",
  "stack",
  "symbol",
  "symbolSize",
  "barMaxWidth",
  "barGap",
  "barCategoryGap",
  "nodeAlign",
  "layoutIterations",
  "leafDepth",
  "roam",
  "orient",
  "sort",
  "min",
  "max",
  "splitNumber",
  "detail",
  "axisLine",
] as const;

/** Keep user presentation tweaks when chart type/orientation template is rebuilt. */
export function mergePresentationOptions(
  previous: Record<string, unknown>,
  base: Record<string, unknown>
): Record<string, unknown> {
  const merged = deepClone(base);
  for (const key of PRESENTATION_KEYS) {
    if (previous[key] === undefined) continue;
    if (
      typeof previous[key] === "object" &&
      previous[key] !== null &&
      !Array.isArray(previous[key]) &&
      typeof merged[key] === "object" &&
      merged[key] !== null
    ) {
      merged[key] = deepMerge(merged[key] as Record<string, unknown>, previous[key] as Record<string, unknown>);
    } else {
      merged[key] = previous[key];
    }
  }

  const prevSeries = previous.series;
  const baseSeries = merged.series;
  if (Array.isArray(prevSeries) && Array.isArray(baseSeries)) {
    merged.series = baseSeries.map((entry, index) => {
      const prev = prevSeries[index];
      if (!prev || typeof prev !== "object" || typeof entry !== "object" || entry === null) {
        return entry;
      }
      const next = deepClone(entry) as Record<string, unknown>;
      const prevObj = prev as Record<string, unknown>;
      for (const sk of SERIES_STYLE_KEYS) {
        if (prevObj[sk] !== undefined) {
          next[sk] = prevObj[sk];
        }
      }
      return next;
    });
  }

  for (const axisKey of ["xAxis", "yAxis"] as const) {
    const prevAxis = previous[axisKey];
    const baseAxis = merged[axisKey];
    if (
      prevAxis &&
      baseAxis &&
      typeof prevAxis === "object" &&
      typeof baseAxis === "object" &&
      !Array.isArray(prevAxis) &&
      !Array.isArray(baseAxis)
    ) {
      merged[axisKey] = deepMerge(baseAxis as Record<string, unknown>, prevAxis as Record<string, unknown>);
    }
  }

  return merged;
}
